/**
 * **적의 대장과 부장은 역사 기록과 뒤 시대의 전승에 이름이 남은 인물이다**(docs/design.md 「적의 부대」). 플레이한 사람이 정했다(2026-10-07): "적 대장과
 * 부장들 실존인물로 두자." 나라마다 여덟 — 총대장 하나와 부장 일곱(적은 여덟 부대까지 선다, `FOE_SQUADS`).
 *
 * - **우리 부하장수와 같은 인물을 적으로도 쓴다**(같은 날: "이거 좋아. 괜찮아."). 백제를 고르면 계백이 부하장수이고,
 *   고구려를 고르면 계백이 백제군의 총대장으로 선다. 그림과 열전이 이미 있다.
 * - 나라마다 다섯을 새로 더했다(명단도 받았다: "새로 더할 후보 명단은 권장안 좋아").
 * - 자리는 부대 번호를 따른다: 0번(가운데) 부대가 총대장, 1번부터 차례로 부장. 부대가 적은 깊이에는 앞사람부터 선다 —
 *   이름난 사람일수록 일찍 만난다.
 *
 * **이름과 소개뿐이다 — 몸은 모두 같다**(`chiefSpec`·`subChiefSpec`). 화면만 읽는 표라 시뮬레이션은 이 파일을 모른다:
 * 누가 서는지는 나라와 부대 번호에서 나오므로 리플레이에 실을 것이 없다. 인물마다 다른 몸·그림은 다음 일이다.
 *
 * 소개는 기록에 적힌 일만 짧게 적는다. 이름이 뒤 시대의 기록에 처음 나오는 사람(양만춘·강이식)은 그렇게 적는다 —
 * 그 둘은 당대의 사서가 이름을 적지 않았다(리뷰가 짚었다: "실존 인물"이라고만 하면 확정된 기록처럼 읽힌다).
 * 우리 부하장수와 같은 사람은 열전(`ui/companion-lore.ts`)에 적은 일에서 벗어나지 않는다.
 *
 * **이름난 사람은 관문에서만 만난다**(`FOE_LEADERS`). 여느 판에는 덜 알려진 장수들이 선다(`FOE_CAPTAINS`). 처음에는 모든
 * 판에 위의 여덟이 섰다 — 깊이 6의 작은 싸움에도 을지문덕이, 깊이 7에도 을지문덕이 나왔다. 플레이한 사람이 짚었다
 * (2026-10-08): "적장이 항상 유명한 장수라서 이상하다. 주요 관문에서만 유명한 장수가 나오고 일반적인 전장에서는 덜 유명한
 * 장수가 나오면 좋겠어. 덜 유명한 장수는 고유 복장은 필요 없어." 덜 알려진 장수는 차림 표(`render/looks.ts`의
 * `FOE_LEADER_LOOK`)에 없어 여느 적장의 모습으로 선다. 판마다 같은 사람이 총대장이지 않게 깊이만큼 돌려 세운다
 * (`foeLeader`) — 깊이 6의 총대장과 깊이 7의 총대장이 다르다.
 */
import { Culture, Role, foeHeadSquad, isGate } from '../sim/troops.ts';
import { Team } from '../sim/units.ts';
import type { World } from '../sim/world.ts';

export interface FoeLeader {
  /** 우리 부하장수와 같은 인물이면 그 id(`COMPANIONS`) — 그림과 열전을 같이 쓴다. */
  readonly id: string;
  readonly name: string;
  /** 누구인지 한 줄(직함·때). */
  readonly who: string;
  /** 사서에 적힌 일 한 줄. */
  readonly note: string;
}

const lead = (id: string, name: string, who: string, note: string): FoeLeader => ({ id, name, who, note });

/** 나라마다 여덟: 맨 앞이 총대장, 나머지가 부장(부대 1~7번의 순서). */
export const FOE_LEADERS: Record<Culture, readonly FoeLeader[]> = {
  [Culture.Goguryeo]: [
    lead('go_eulji', '을지문덕', '영양왕 때의 장군', '612년 살수에서 수나라의 대군을 크게 깨뜨렸다.'),
    lead('go_yeon', '연개소문', '고구려 말의 대막리지', '642년 정변으로 나라의 권력을 쥐고 당나라의 침입에 맞섰다.'),
    lead('go_ondal', '온달', '평원왕 때의 장군', '바보라 불리다 평강공주를 만나 이름을 알렸고, 뒤에 잃은 땅을 되찾겠다며 나섰다.'),
    lead('go_yang', '양만춘', '안시성의 성주', '645년 당 태종의 포위를 버텨 냈다. 이름은 뒤 시대의 기록에 처음 나온다.'),
    lead('go_myeongnim', '명림답부', '신대왕 때의 국상', '들을 비우고 성을 지키다, 굶주려 물러가는 한나라 군을 좌원에서 쳤다.'),
    lead('go_euldudji', '을두지', '대무신왕의 좌보', '한나라 군이 성을 에워싸자 연못의 잉어와 술을 보내 물이 넉넉함을 보여 물러가게 했다.'),
    lead('go_gang', '강이식', '영양왕 때의 장수로 전하는 인물', '598년 수나라와의 싸움에 나섰다고 뒤 시대의 기록이 전한다.'),
    lead('go_goyeonsu', '고연수', '북부의 욕살', '645년 안시성을 구하러 가다 주필산에서 당나라 군에 졌다.'),
  ],
  [Culture.Baekje]: [
    lead('bj_gyebaek', '계백', '의자왕 때의 달솔', '660년 황산벌에서 결사대 오천으로 신라의 대군을 네 번 막았다.'),
    lead('bj_heukchi', '흑치상지', '백제의 달솔 겸 풍달군장', '나라가 무너진 뒤 임존성에서 군사를 모아 이백여 성을 되찾았다.'),
    lead('bj_seongchung', '성충', '의자왕 때의 좌평', '옥에서 죽어 가며 탄현과 기벌포를 막으라는 글을 남겼다.'),
    lead('bj_yunchung', '윤충', '의자왕 때의 장군', '642년 대야성을 함락했다.'),
    lead('bj_uijik', '의직', '의자왕 때의 장군', '648년 신라의 서쪽 변경을 쳐 여러 성을 빼앗았다.'),
    lead('bj_eunsang', '은상', '의자왕 때의 장군', '649년 석토성을 비롯한 신라의 일곱 성을 쳤다.'),
    lead('bj_boksin', '복신', '무왕의 조카', '나라가 무너진 뒤 주류성에서 부흥군을 일으켰다.'),
    lead('bj_jisusin', '지수신', '부흥군의 장수', '663년 임존성에서 끝까지 저항했다.'),
  ],
  [Culture.Silla]: [
    lead('sl_yusin', '김유신', '가야 왕족의 후손', '선덕여왕 때부터 문무왕 때까지 신라의 군사를 이끌었다.'),
    lead('sl_alcheon', '알천', '선덕여왕 때의 장군', '옥문곡에 숨어든 백제 군사 오백을 찾아 쳐 없앴다.'),
    lead('sl_heumsun', '김흠순', '김유신의 아우', '660년 황산벌에서 장군으로 싸웠다.'),
    lead('sl_pumil', '품일', '황산벌의 장군', '660년 황산벌에서 아들 관창을 적진으로 내보냈다.'),
    lead('sl_jukjuk', '죽죽', '대야성의 사지', '642년 대야성이 무너질 때 끝까지 싸우다 죽었다.'),
    lead('sl_chunchu', '김춘추', '뒤의 태종무열왕', '당나라와 손을 잡는 길을 열었다.'),
    lead('sl_gwanchang', '관창', '품일의 아들, 화랑', '660년 황산벌에서 홀로 적진에 뛰어들었다.'),
    lead('sl_binyeongja', '비녕자', '진덕여왕 때의 군인', '647년 무산성 싸움에서 적진에 뛰어들어 죽었다.'),
  ],
};

/**
 * 여느 판(관문이 아닌 깊이)의 대장들 — 나라마다 여덟. 사서에 이름이 남았지만 덜 알려진 장수들이다. 소개는 기록에 적힌 일만
 * 짧게 적는다(여자진은 일본 쪽 기록에 전한다 — 그렇게 적었다). 리뷰가 사서와 대조해 셋을 고쳤다: 상영은 달솔이 아니라
 * 좌평이고, 고노자가 모용외를 싸워 물리친 것은 한 번이며(두 번째는 태수가 된 뒤 침입이 그쳤다), 찬덕이 죽은 것은 항전을
 * 시작한 이듬해다. 연도는 『삼국사기』의 기년을 따른다. 우리 부하장수와 겹치는 사람은 없다 — 고유한 그림도 없다.
 */
export const FOE_CAPTAINS: Record<Culture, readonly FoeLeader[]> = {
  [Culture.Goguryeo]: [
    lead('go_bubunno', '부분노', '동명성왕 때의 장수', '행인국을 쳤고, 유리왕 때 꾀를 내어 선비를 눌렀다.'),
    lead('go_gonoja', '고노자', '봉상왕 때의 신성 태수', '모용외의 군대를 물리쳐 왕을 구했고, 뒤에 신성 태수가 되어 침입을 막았다.'),
    lead('go_yuyu', '유유', '동천왕 때의 동부 사람', '246년 위나라 장수를 찌르고 함께 죽어 왕을 구했다.'),
    lead('go_goseung', '고승', '영양왕 때의 장군', '603년 신라의 북한산성을 쳤다.'),
    lead('go_gohyejin', '고혜진', '남부 욕살', '645년 고연수와 함께 안시성을 구하러 나섰다.'),
    lead('go_gojeongui', '고정의', '보장왕 때의 대로', '645년 싸움을 서두르지 말고 오래 버티자고 권했다.'),
    lead('go_onsamun', '온사문', '보장왕 때의 장수', '659년 횡산에서 당나라 군대와 싸웠다.'),
    lead('go_noeeumsin', '뇌음신', '보장왕 때의 장군', '661년 신라의 북한산성을 에워쌌다.'),
  ],
  [Culture.Baekje]: [
    lead('bj_haesu', '해수', '무왕 때의 좌평', '602년 아막성 싸움을 이끌었다.'),
    lead('bj_baekgi', '백기', '무왕 때의 달솔', '616년 신라의 모산성을 쳤다.'),
    lead('bj_uso', '우소', '무왕 때의 장군', '636년 독산성을 치러 가다 옥문곡에서 패했다.'),
    lead('bj_chungsang', '충상', '의자왕 때의 좌평', '660년 황산벌에서 계백과 함께 싸웠다.'),
    lead('bj_sangyeong', '상영', '의자왕 때의 좌평', '660년 황산벌에서 싸우다 사로잡혔다.'),
    lead('bj_sata', '사타상여', '백제 부흥군의 장수', '흑치상지와 함께 임존성에서 일어났다.'),
    lead('bj_dochim', '도침', '승려, 백제 부흥군의 우두머리', '복신과 함께 부흥군을 이끌고 주류성을 근거로 삼았다.'),
    lead('bj_yeojajin', '여자진', '백제 부흥군의 장수', '660년 구마노리성에서 버텼다고 일본 쪽 기록이 전한다.'),
  ],
  [Culture.Silla]: [
    lead('sl_seohyeon', '김서현', '진평왕 때의 장군, 김유신의 아버지', '629년 고구려의 낭비성을 쳤다.'),
    lead('sl_geonpum', '건품', '진평왕 때의 파진찬', '602년 아막성 싸움을 이끌었다.'),
    lead('sl_mueun', '무은', '진평왕 때의 장군, 귀산의 아버지', '602년 아막성에서 싸웠다.'),
    lead('sl_chandeok', '찬덕', '가잠성의 현령', '611년부터 백 일 남짓 가잠성을 지키다 이듬해 죽었다.'),
    lead('sl_haeron', '해론', '찬덕의 아들', '618년 가잠성을 되찾는 싸움에서 죽었다.'),
    lead('sl_nulchoe', '눌최', '진평왕 때의 장수', '624년 백제의 공격에 끝까지 성을 지키다 죽었다.'),
    lead('sl_cheonjon', '천존', '문무왕 때의 장군', '백제와 고구려를 치는 싸움에 여러 번 나섰다.'),
    lead('sl_jukji', '죽지', '화랑 출신의 장군', '649년 도살성 싸움에 나섰고 뒤에 첫 중시가 되었다.'),
  ],
};

/**
 * 그 나라 군대의 `squad`번 부대의 대장(0번이 총대장). 칸 밖이면 null. `depth`가 관문이면 이름난 여덟(`FOE_LEADERS`)이
 * 부대 번호대로 서고, 아니면 덜 알려진 여덟(`FOE_CAPTAINS`)이 **깊이만큼 돌아** 선다 — 판마다 총대장이 달라진다.
 * 깊이를 주지 않으면 이름난 여덟이다(시험과 옛 호출).
 */
export function foeLeader(culture: Culture, squad: number, depth?: number): FoeLeader | null {
  const famed = depth === undefined || isGate(Math.floor(depth));
  const list = famed ? FOE_LEADERS[culture] : FOE_CAPTAINS[culture];
  if (!Number.isInteger(squad) || squad < 0 || !list || squad >= list.length) return null;
  return famed ? list[squad] : list[(squad + Math.max(0, Math.floor(depth!))) % list.length];
}

/** 전장의 그 유닛이 적의 대장이면 누구인가. 토벌의 두령·대장이 아닌 유닛은 null. */
export function foeLeaderOf(w: World, i: number): FoeLeader | null {
  if (i < 0 || i >= w.count || w.team[i] !== Team.Enemy || w.role[i] !== Role.Chief) return null;
  if (w.foeCmd[w.foeSquad[i]] !== i) return null;
  const spec = w.specs[w.specIdx[i]];
  // 토벌의 두령은 나라의 장수가 아니다.
  if (spec.troop !== 'hero_chief' && spec.troop !== 'hero_subchief') return null;
  return foeLeader(spec.culture, w.foeSquad[i], w.depth);
}

/** 그 깊이의 판에 서는 대장들(총대장이 맨 앞) — 부대 수만큼. 깊이를 주지 않으면 이름난 여덟에서(`foeLeader`). */
export function foeLeadersFor(culture: Culture, squads: number, depth?: number): readonly FoeLeader[] {
  const n = Math.max(1, Math.min(FOE_LEADERS[culture].length, Math.floor(squads)));
  return Array.from({ length: n }, (_, sq) => foeLeader(culture, sq, depth)!);
}

/**
 * 방금 쓰러진 대장들을 직함으로 갈라 모은다: 적장(`heads` — 총대장, 결전은 0·1·2번 부대의 대장)과 부장(`subs`). `was`는
 * 지난번에 본 부대별 생존(부르는 쪽이 들고 있다 — 여기서 지금 값으로 고쳐 적는다). 전장의 알림과 다시 보기의 순간
 * 목록이 같이 쓴다 — 두 곳이 저마다 "0번이면 적장, 결전이면 모두 적장"으로 갈랐는데, 결전에 부장을 세우며(REPLAY 118)
 * 그 가름을 `foeHeadSquad` 하나로 모았다.
 */
export function fallenLeaders(w: World, was: boolean[]): { heads: string[]; subs: string[] } {
  const heads: string[] = [];
  const subs: string[] = [];
  for (let sq = 0; sq < was.length; sq++) {
    const c = w.foeCmd[sq];
    const up = c >= 0 && w.alive[c] === 1;
    if (was[sq] && !up) {
      const who = foeLeaderOf(w, c);
      if (who) (foeHeadSquad(sq, w.warlords) ? heads : subs).push(who.name);
    }
    was[sq] = up;
  }
  return { heads, subs };
}

/**
 * 쓰러진 대장들을 알리는 글머리: "적장 계백이 쓰러졌다", "적 부장 성충·윤충이 쓰러졌다", 적장과 부장이 한 틱에 함께면
 * "적장 계백과 부장 흑치상지가 쓰러졌다". **부장만이면 "적 부장"으로 적는다** — "부장 김흠순이 쓰러졌다"는 우리 사람이
 * 쓰러진 것으로 읽힐 수 있었다(플레이한 사람, 2026-10-07; 우리 관등에도 부장이 있다). 적장과 함께면 앞의 "적장"이 편을 알린다. 적장 결전은 적장이 셋이라 "적장 김유신·알천이"도 나온다. 이름이 하나도
 * 없으면 빈 글(부르는 쪽이 예전 문구를 쓴다). 처음에는 이름을 한 줄로 합친 뒤 총대장이 쓰러졌는지로 직함을 골라,
 * 함께 쓰러진 부장까지 "적장"으로 적었다(리뷰·QA가 짚었다).
 */
export function fallTitle(heads: readonly string[], subs: readonly string[]): string {
  if (heads.length > 0 && subs.length > 0) return `적장 ${withAnd(heads.join('·'))} 부장 ${withSubject(subs.join('·'))} 쓰러졌다`;
  if (heads.length > 0) return `적장 ${withSubject(heads.join('·'))} 쓰러졌다`;
  return subs.length > 0 ? `적 부장 ${withSubject(subs.join('·'))} 쓰러졌다` : '';
}

/** 받침에 맞는 "와/과"를 붙인다: "계백과", "김춘추와". 한글이 아니면 "와(과)". */
export function withAnd(name: string): string {
  const c = name.charCodeAt(name.length - 1);
  if (c < 0xac00 || c > 0xd7a3) return `${name}와(과)`;
  return (c - 0xac00) % 28 === 0 ? `${name}와` : `${name}과`;
}

/** 받침에 맞는 주격 조사를 붙인다: "계백이", "을지문덕이", "윤충이", "김춘추가". 한글이 아니면 "이(가)". */
export function withSubject(name: string): string {
  const c = name.charCodeAt(name.length - 1);
  if (c < 0xac00 || c > 0xd7a3) return `${name}이(가)`;
  return (c - 0xac00) % 28 === 0 ? `${name}가` : `${name}이`;
}
